import React from 'react';
import { motion } from 'framer-motion';

// Image imports
const brewerydbHero1 = '/images/brewerydb-hero-1.png';
const brewerydbHero2Image = '/images/brewerydb-hero-2-image.png';
const brewerydbHero4Image = '/images/brewerydb-hero-4-image.png';
const brewerydbHero5Component = '/images/brewerydb-hero-5-component.png';
const brewerydbUserResearch = '/images/brewerydb-user-research.png';
const brewerydbDesignSystemFigma = '/images/brewerydb-design-system-figma.png';
const brewerydbOnboarding = '/images/brewerydb-onboarding.png';
const brewerydbPresentation1 = '/images/brewerydb-presentation-1.png';
const brewerydbPresentation2 = '/images/brewerydb-presentation-2.png';
const brewerydbPresentation3 = '/images/brewerydb-presentation-3.png';
const brewerydbPresentation4 = '/images/brewerydb-presentation-4.png';

// Video imports
const brewerydbBreweryRoutes = '/video/brewerydb-breweryroutes.mp4';
const brewerydbMap = '/video/brewerydb-map.mp4';

const BreweryDB: React.FC = () => {
  return (
    <div className="min-h-screen text-black dark:text-white">
      <div className="mx-auto xl:max-w-8xl flex flex-1 flex-col gap-2 p-4 md:p-6">
        <section className="grid md:grid-cols-12 my-4 md:my-8">
          <div className="col-span-12 lg:col-start-3 lg:col-span-8 2xl:col-start-4 2xl:col-span-6 space-y-6 md:space-y-12">
            <motion.h1
              className="md:!leading-tight text-3xl md:text-4xl"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              viewport={{ once: true, amount: 0.3 }}
            >
              BreweryDB grew new user accounts by 25% with map discovery and brewery route planning.
            </motion.h1>
            <motion.div
              className="grid md:grid-cols-12 md:gap-6 space-y-4 md:space-y-0 col-span-12"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              viewport={{ once: true, amount: 0.3 }}
            >
              <div className="md:col-span-5 space-y-4 md:space-y-8">
                <div className="space-y-1 md:space-y-3">
                  <p className="font-mono-recursive md:text-lg">Team</p>
                  <p className="md:text-lg dark:dark:text-zinc-400">Worked closely with engineers and product managers.</p>
                </div>
                <div className="space-y-1 md:space-y-3">
                  <p className="font-mono-recursive md:text-lg">Tools</p>
                  <p className="md:text-lg dark:dark:text-zinc-400">Figma<span className="mx-1"> · </span>Axure RP<span className="mx-1"> · </span>Origami Studio</p>
                </div>
                <div className="space-y-1 md:space-y-3">
                  <p className="font-mono-recursive md:text-lg">Date</p>
                  <p className="md:text-lg dark:dark:text-zinc-400">2024</p>
                </div>
              </div>
              <div className="md:col-span-7 space-y-4 md:space-y-8">
                <div className="space-y-1 md:space-y-3">
                  <p className="font-mono-recursive md:text-lg">Role & Contributions</p>
                  <p className="md:text-lg dark:dark:text-zinc-400">Product Designer<span className="mx-1"> · </span>User Research<span className="mx-1"> · </span>Information Architecture<span className="mx-1"> · </span>Prototyping</p>
                </div>
                <div className="space-y-1 md:space-y-3">
                  <p className="font-mono-recursive md:text-lg">Key Achievements</p>
                  <p className="md:text-lg dark:dark:text-zinc-400">Designed and shipped a native iOS and Android experience built around map discovery, brewery route planning, and shareable boards. The onboarding flow and core feature set drove a 25% increase in new user accounts, with stronger engagement in board creation and positive feedback on route-building for trips.</p>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        <section className="mt-8 md:mt-14">
          <motion.div
            className="col-span-3 grid md:grid-cols-12 gap-4 md:grid-rows-2"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            viewport={{ once: true, amount: 0.3 }}
          >
            <div className="block md:hidden md:col-span-5 flex flex-col justify-centerl">
              <div className="overflow-hidden rounded-lg md:rounded-2xl">
                <motion.img 
                  src={brewerydbHero1} 
                  alt="BreweryDB Hero 1"
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  transition={{ duration: 0.6, ease: "easeOut", delay: 0.1 }}
                  viewport={{ once: true, amount: 0.3 }}
                />
              </div>
            </div>
            <div className="hidden md:block md:col-span-5 flex flex-col justify-center min-h-0">
              <div className="overflow-hidden rounded-lg md:rounded-2xl h-full">
                <motion.iframe 
                  width='100%' 
                  className="w-full h-full"
                  src="https://api.mapbox.com/styles/v1/samuels8833/clmkv0r9b04e101qxc3ed9xwa.html?title=false&access_token=pk.eyJ1Ijoic2FtdWVsczg4MzMiLCJhIjoiY2w0eTRyaHUxMXVneDNkb2JxeGhseWZtMiJ9.vTphURWspkHA_6_3_9sYfw&zoomwheel=false#11.13/42.3469/-83.0456"
                  title="BDB Map Styles v2" 
                  style={{border: 'none'}}
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  transition={{ duration: 0.6, ease: "easeOut", delay: 0.2 }}
                  viewport={{ once: true, amount: 0.3 }}
                />
              </div>
            </div>
            <div className="md:col-span-3 flex flex-col justify-center bg-[#C8F068] overflow-hidden rounded-lg md:rounded-2xl">
              <div className="p-12 md:p-6 lg:p-8">
                <motion.img 
                  src={brewerydbHero2Image} 
                  alt="BreweryDB Hero 2"
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  transition={{ duration: 0.6, ease: "easeOut", delay: 0.3 }}
                  viewport={{ once: true, amount: 0.3 }}
                />
              </div>
            </div>
            <div className="md:col-span-4 md:row-span-2 flex flex-col justify-center bg-gray-100 overflow-hidden rounded-lg md:rounded-2xl">
              <div className="sm:aspect-video px-4 py-6 sm:p-6">
                <motion.video 
                  className="w-full h-full" 
                  autoPlay 
                  loop 
                  muted 
                  playsInline
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  transition={{ duration: 0.6, ease: "easeOut", delay: 0.4 }}
                  viewport={{ once: true, amount: 0.3 }}
                >
                  <source src={brewerydbBreweryRoutes} type="video/mp4" />
                  Your browser does not support the video tag.
                </motion.video>
              </div>
            </div>
            <div className="md:col-span-3 flex flex-col justify-center bg-blue-100 overflow-hidden rounded-lg md:rounded-2xl">
              <div className="p-6 md:p-8">
                <motion.img 
                  src={brewerydbHero4Image} 
                  alt="BreweryDB Hero 4"
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  transition={{ duration: 0.6, ease: "easeOut", delay: 0.5 }}
                  viewport={{ once: true, amount: 0.3 }}
                />
              </div>
            </div>
            <div className="md:col-span-5 flex flex-col justify-center bg-gray-100 overflow-hidden rounded-lg md:rounded-2xl">
              <div className="p-6 md:p-8">
                <motion.img 
                  src={brewerydbHero5Component} 
                  alt="BreweryDB Hero 5"
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  transition={{ duration: 0.6, ease: "easeOut", delay: 0.6 }}
                  viewport={{ once: true, amount: 0.3 }}
                />
              </div>
            </div>
          </motion.div>

          <div className="grid md:grid-cols-12 my-12 md:my-28 space-y-12 md:space-y-28">
            <motion.div
              className="col-span-12 lg:col-start-3 lg:col-span-8 2xl:col-start-4 2xl:col-span-6 space-y-4"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              viewport={{ once: true, amount: 0.3 }}
            >
              <h2 className="text-xl md:text-2xl font-medium">Objective</h2>
              <p className="lg:leading-normal text-md md:text-lg dark:text-zinc-400">
                The goal was to create an engaging native app for iOS and Android to enhance the craft beer experience for enthusiasts. The app was designed to allow users to search and explore breweries and brews on an interactive map, plan brewery routes for trips using flexible map route-building tools, and leverage native features such as location sharing and push notifications for personalized experiences. Additional features included intuitive onboarding, a curated explore screen, the ability to create and share personalized boards, and brewery-specific filters for a seamless and enjoyable user experience.
              </p>
            </motion.div>

            <motion.figure
              className="col-span-12"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.6, ease: "easeOut", delay: 0.1 }}
              viewport={{ once: true, amount: 0.3 }}
            >
              <motion.img 
                className="overflow-hidden rounded-lg md:rounded-2xl" 
                src={brewerydbUserResearch} 
                alt="User research"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 0.6, ease: "easeOut", delay: 0.2 }}
                viewport={{ once: true, amount: 0.3 }}
              />
              <figcaption className="text-center italic pt-2 text-gray-500 font-mono-recursive">
                User research
              </figcaption>
            </motion.figure>

            <motion.div
              className="col-span-12 lg:col-start-3 lg:col-span-8 2xl:col-start-4 2xl:col-span-6 space-y-4"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              viewport={{ once: true, amount: 0.3 }}
            >
              <h2 className="text-xl md:text-2xl font-medium">Discovery & Research</h2>
              <p className="lg:leading-normal text-md md:text-lg dark:text-zinc-400">The project began with user research through interviews and surveys with beer enthusiasts and travelers to understand their pain points and preferences. Competitive analysis of similar mapping and travel apps, such as Google Maps for route building and Yelp for discovery, provided insights into best practices.</p>
            </motion.div>

            <motion.figure
              className="col-span-12"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.6, ease: "easeOut", delay: 0.1 }}
              viewport={{ once: true, amount: 0.3 }}
            >
              <motion.img 
                className="overflow-hidden" 
                src={brewerydbDesignSystemFigma} 
                alt="Design system in Figma"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 0.6, ease: "easeOut", delay: 0.2 }}
                viewport={{ once: true, amount: 0.3 }}
              />
            </motion.figure>

            <motion.div
              className="col-span-12 lg:col-start-3 lg:col-span-8 2xl:col-start-4 2xl:col-span-6 space-y-4"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              viewport={{ once: true, amount: 0.3 }}
            >
              <h2 className="text-xl md:text-2xl font-medium">Design Solutions</h2>
              <p className="lg:leading-normal text-md md:text-lg dark:text-zinc-400">To simplify the complex brewery data, a backend strategy was developed with the engineering team to standardize data formats. Modular UI cards were designed to dynamically adapt to available data, with fallback elements for missing information. Flexible navigation was crafted with an intuitive tab-bar system prioritizing Explore, Map, Boards, and Profile. Gestural controls ensured smooth map interactions with quick filters.</p>
              <p className="lg:leading-normal text-md md:text-lg dark:text-zinc-400">The visual identity adopted a warm, earthy color palette and modern typography to align with the craft beer aesthetic. Visual hierarchy was emphasized through large brewery thumbnails and clear call-to-action buttons, enhancing usability.</p>
            </motion.div>

            <motion.figure
              className="col-span-12"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.6, ease: "easeOut", delay: 0.1 }}
              viewport={{ once: true, amount: 0.3 }}
            >
              <motion.img 
                className="overflow-hidden rounded-lg md:rounded-2xl" 
                src={brewerydbOnboarding} 
                alt="Onboarding user flows"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 0.6, ease: "easeOut", delay: 0.2 }}
                viewport={{ once: true, amount: 0.3 }}
              />
              <figcaption className="text-center italic pt-2 text-gray-500 font-mono-recursive">
                Onboarding user flows
              </figcaption>
            </motion.figure>

            <motion.div
              className="col-span-12 lg:col-start-3 lg:col-span-8 2xl:col-start-4 2xl:col-span-6 space-y-4"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              viewport={{ once: true, amount: 0.3 }}
            >
              <h2 className="text-xl md:text-2xl font-medium">Key Features</h2>
              <p className="lg:leading-normal text-md md:text-lg dark:text-zinc-400">The app featured an interactive onboarding experience with a simple, visual flow that helped users quickly understand its benefits. Onboarding progressively revealed features such as location sharing and notification preferences. A curated explore screen displayed nearby breweries and brews based on user location, activity, and promotions, with dynamic content blocks showcasing trending breweries and limited-time offers.</p>
            </motion.div>

            <motion.figure
              className="col-span-12"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.6, ease: "easeOut", delay: 0.1 }}
              viewport={{ once: true, amount: 0.3 }}
            >
              <div className="flex min-h-full flex-col justify-center bg-gray-100 rounded-lg md:rounded-2xl">
                <div className="sm:px-6 lg:col-span-12 lg:px-0">
                  <div className="aspect-video px-4 py-6 sm:p-6">
                    <motion.video 
                      className="w-full h-full" 
                      autoPlay 
                      loop 
                      muted 
                      playsInline
                      initial={{ opacity: 0 }}
                      whileInView={{ opacity: 1 }}
                      transition={{ duration: 0.6, ease: "easeOut", delay: 0.2 }}
                      viewport={{ once: true, amount: 0.3 }}
                    >
                      <source src={brewerydbMap} type="video/mp4" />
                      Your browser does not support the video tag.
                    </motion.video>
                  </div>
                </div>
              </div>
              <figcaption className="text-center italic pt-2 text-gray-500 font-mono-recursive">
                BreweryDB map prototype
              </figcaption>
            </motion.figure>

            <motion.div
              className="col-span-12 lg:col-start-3 lg:col-span-8 2xl:col-start-4 2xl:col-span-6 space-y-4"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              viewport={{ once: true, amount: 0.3 }}
            >
              <p className="lg:leading-normal text-md md:text-lg dark:text-zinc-400">An interactive map with brewery-specific filters allowed users to filter by type, such as microbreweries or brewpubs, or specific offerings like dog-friendly spaces or food service. Breweries were displayed with distinct icons for easy navigation. The map route builder enabled users to create routes with multi-stop functionality, adjusting brewery selections based on radius and preferences while providing travel times, brewery descriptions, and custom alerts for deviations or optimizations.</p>
              <p className="lg:leading-normal text-md md:text-lg dark:text-zinc-400">Users could create and share boards of their favorite breweries and brews, adding a social feature for collaboration and discovery among friends. Native integrations included push notifications to alert users when near saved breweries or new promotions and location sharing for real-time updates with friends on trips.</p>
            </motion.div>

            <motion.figure
              className="col-span-12"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.6, ease: "easeOut", delay: 0.1 }}
              viewport={{ once: true, amount: 0.3 }}
            >
              <motion.img 
                className="overflow-hidden rounded-lg md:rounded-2xl" 
                src={brewerydbPresentation1} 
                alt="BreweryDB Presentation 1"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 0.6, ease: "easeOut", delay: 0.2 }}
                viewport={{ once: true, amount: 0.3 }}
              />
            </motion.figure>

            <motion.div
              className="col-span-12 lg:col-start-3 lg:col-span-8 2xl:col-start-4 2xl:col-span-6 space-y-4"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              viewport={{ once: true, amount: 0.3 }}
            >
              <h2 className="text-xl md:text-2xl font-medium">Outcome</h2>
              <p className="lg:leading-normal text-md md:text-lg dark:text-zinc-400">The BreweryDB native app achieved significant success, with a 25% increase in new user accounts within the first three months driven by the engaging onboarding experience and feature set. User activity saw a notable boost, especially in creating and sharing boards, which fostered a social component to the craft beer experience. Positive user feedback highlighted the ease of discovering breweries and the utility of the route-building feature for travel planning.</p>
            </motion.div>

            <motion.figure
              className="col-span-12"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.6, ease: "easeOut", delay: 0.1 }}
              viewport={{ once: true, amount: 0.3 }}
            >
              <motion.img 
                className="overflow-hidden rounded-lg md:rounded-2xl" 
                src={brewerydbPresentation2} 
                alt="BreweryDB Presentation 2"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 0.6, ease: "easeOut", delay: 0.2 }}
                viewport={{ once: true, amount: 0.3 }}
              />
            </motion.figure>

            <motion.figure
              className="col-span-12"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.6, ease: "easeOut", delay: 0.1 }}
              viewport={{ once: true, amount: 0.3 }}
            >
              <motion.img 
                className="overflow-hidden rounded-lg md:rounded-2xl" 
                src={brewerydbPresentation3} 
                alt="BreweryDB Presentation 3"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 0.6, ease: "easeOut", delay: 0.2 }}
                viewport={{ once: true, amount: 0.3 }}
              />
            </motion.figure>

            <motion.figure
              className="col-span-12"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.6, ease: "easeOut", delay: 0.1 }}
              viewport={{ once: true, amount: 0.3 }}
            >
              <motion.img 
                className="overflow-hidden rounded-lg md:rounded-2xl" 
                src={brewerydbPresentation4} 
                alt="BreweryDB Presentation 4"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 0.6, ease: "easeOut", delay: 0.2 }}
                viewport={{ once: true, amount: 0.3 }}
              />
            </motion.figure>
          </div>
        </section>
      </div>
    </div>
  );
};

export default BreweryDB;
